import axios from 'axios'

import type { ClientUser, Settings } from '../../shared/types/user'
import { claimCollectionCache, clearCollectionCache } from '../cache/collection'
import {
	clearAllSettingsCache,
	getPendingSettingsCache,
	setSettingsCache,
} from '../cache/settings'

let cachedUser: ClientUser | null | undefined
let pendingUserRequest: Promise<ClientUser | null> | null = null
let userRequestSequence = 0

/**
 * Returns whether an authenticated user is currently cached in memory.
 */
export function hasAuthenticatedUser(): boolean {
	return !!cachedUser
}

/**
 * Clears the in-memory user cache and any in-flight user lookup.
 */
export function clearUserCache(signedOut = false): void {
	userRequestSequence++
	cachedUser = signedOut ? null : undefined
	pendingUserRequest = null
}

/**
 * Applies a partial settings update to the in-memory authenticated user, when present.
 */
export function updateCachedUserSettings(settings: Settings): void {
	if (!cachedUser) return
	cachedUser = {
		...cachedUser,
		settings,
	}
}

/**
 * Resolves the current authenticated user, with in-memory caching and request deduplication.
 */
export async function getUser(options?: {
	force?: boolean
}): Promise<ClientUser | null> {
	if (!options?.force && typeof cachedUser !== 'undefined') {
		return cachedUser
	}

	if (!options?.force && pendingUserRequest) {
		return pendingUserRequest
	}

	const requestSequence = ++userRequestSequence
	const request = (async () => {
		const previousUser = cachedUser
		try {
			const { data } = await axios.get('/auth/me')
			if (requestSequence !== userRequestSequence) {
				return resolveCurrentUser()
			}
			claimCollectionCache(String((data as ClientUser)._id))
			const settings =
				getPendingSettingsCache() ?? (data.settings as Settings)
			setSettingsCache(settings)
			cachedUser = {
				...(data as ClientUser),
				settings,
			}
			return cachedUser
		} catch (err) {
			if (requestSequence !== userRequestSequence) {
				return resolveCurrentUser()
			}
			if (axios.isAxiosError(err) && err.response?.status === 401) {
				clearAllSettingsCache()
				clearCollectionCache()
				cachedUser = null
			} else {
				cachedUser =
					typeof previousUser === 'undefined' ? null : previousUser
			}
			console.error('Auth check failed', err)
			return cachedUser
		} finally {
			if (requestSequence === userRequestSequence) {
				pendingUserRequest = null
			}
		}
	})()
	pendingUserRequest = request

	return request
}

/** Joins the latest lookup when an older request finishes after invalidation. */
function resolveCurrentUser(): Promise<ClientUser | null> {
	if (pendingUserRequest) return pendingUserRequest
	if (typeof cachedUser !== 'undefined') return Promise.resolve(cachedUser)
	return getUser()
}
